import React, {
 useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { uploadVideoToCloudinary } from 'views_shared/app/javascript/api/videoUploads';
import RichTextComposer from '../RichTextComposer';
import AttachmentItem from './AttachmentItem';
import ConfirmVideoDeleteModal from './ConfirmVideoDeleteModal';
import VideoRecorderModal from '../VideoRecorderModal';
import VideoAttachmentItem from '../VideoRecorderModal/VideoAttachmentItem';
import { useMessagesActions, useMessagesState } from '../MessagesContext';
import { MAX_MESSAGE_LENGTH, strippedMessageLength } from '../MessagesContext/useMessages';
import { REQUEST_VIDEO_MESSAGE, OPEN_RECORDER_EVENT } from '../videoRequest';

// Keep the counter hidden for short replies; reveal it once it's long enough to matter.
const CHAR_COUNTER_REVEAL_AT = 1000;

// Matches Interact::Attachment::MAX_PER_ATTACHABLE.
const MAX_ATTACHMENTS = 10;

// Cap each file at 5 MB: the V1->V2 path uploads to Cloudinary twice (V2 row + V1 FDW mirror)
// inside Heroku's 30s router window, so larger files routinely H27. Mirrored cap to V2 for parity.
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Video uploads go browser->Cloudinary directly (not through Rails), so they aren't bound by the 5 MB
// file cap. The recorder enforces the duration cap structurally via auto-stop.
const MAX_VIDEO_SECONDS = 120;
const MAX_VIDEO_SIZE_MB = 100;
const MAX_VIDEO_SIZE_BYTES = MAX_VIDEO_SIZE_MB * 1024 * 1024;

// Some containers fire neither loadedmetadata nor error; bound the probe so a pick can't hang.
const DURATION_PROBE_TIMEOUT_MS = 5000;

// Read a video file's duration from its metadata without decoding the whole file. Resolves null
// when the duration isn't knowable (e.g. some MediaRecorder WebM reports Infinity until seeked, the
// browser can't read it, or metadata never arrives within the timeout) so callers fall back to the
// size cap. Always frees the object URL and settles exactly once.
const probeVideoDuration = (blob) => new Promise((resolve) => {
  const url = URL.createObjectURL(blob);
  const video = document.createElement('video');
  video.preload = 'metadata';
  let settled = false;
  let timer;
  const finish = (value) => {
    if (settled) {
      return;
    }
    settled = true;
    clearTimeout(timer);
    URL.revokeObjectURL(url);
    resolve(value);
  };
  video.onloadedmetadata = () => finish(Number.isFinite(video.duration) ? video.duration : null);
  video.onerror = () => finish(null);
  timer = setTimeout(() => finish(null), DURATION_PROBE_TIMEOUT_MS);
  video.src = url;
});

function MessageComposer({ disabled = false, conversation = null, sendButtonPortal = null }) {
  const {
    messageContent, attachments = [], videoRequestIntent = false, sendState = 'idle',
  } = useMessagesState();
  const {
    setMessageContent, setAttachments, sendMessage, setVideoRequestIntent,
  } = useMessagesActions();
  const currentSlug = globalThis.Qwoted.CURRENT_SLUG;
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [showRecorder, setShowRecorder] = useState(false);
  // Video chip pending trash-confirmation: { index, recorded } or null (file chips remove instantly).
  const [videoPendingDelete, setVideoPendingDelete] = useState(null);
  // The video currently uploading to Cloudinary (not yet in `attachments`): { filename, bytes, progress, localUrl }.
  const [videoUploading, setVideoUploading] = useState(null);
  // Object URLs for local video previews; revoked on remove and on unmount so they don't leak.
  const localVideoUrlsRef = useRef([]);

  // Revoke any tracked local blob URL no longer referenced by a chip or the in-flight upload, so
  // previews made with URL.createObjectURL are freed on send (attachments clear), on remove and on
  // replace — not only on unmount, which used to pin every sent clip's Blob in memory until navigation.
  useEffect(() => {
    const live = new Set([...attachments.map((a) => a.localUrl), videoUploading?.localUrl].filter(Boolean));
    localVideoUrlsRef.current = localVideoUrlsRef.current.filter((url) => {
      if (live.has(url)) {
        return true;
      }
      URL.revokeObjectURL(url);
      return false;
    });
  }, [attachments, videoUploading]);

  // Final sweep on unmount.
  useEffect(() => () => localVideoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url)), []);

  // A received "Request a video" message renders a CTA card (VideoRequestCard) up in the thread;
  // it opens this composer's recorder so recording/sending follows the normal attach flow.
  useEffect(() => {
    const openRecorder = () => {
      if (!disabled && sendState !== 'sending') {
        setShowRecorder(true);
      }
    };
    window.addEventListener(OPEN_RECORDER_EVENT, openRecorder);
    return () => window.removeEventListener(OPEN_RECORDER_EVENT, openRecorder);
  }, [disabled, sendState]);

  const handleMessageChange = (event) => {
    setMessageContent(event.target.value);
  };

  const isMedia = globalThis.Qwoted.CURRENT_PROFILE_TYPE === 'type_media';
  const noMessageText = !messageContent || messageContent === '<br>' || messageContent === '<div><br></div>';

  // Toggle, not a one-shot: the button IS the request state (stamped into message metadata at
  // send); the text is a convenience prefill on first toggle-on and carries no meaning of its own.
  const handleRequestVideo = () => {
    if (videoRequestIntent) {
      setVideoRequestIntent(false);
      return;
    }
    setVideoRequestIntent(true);
    if (noMessageText) {
      setMessageContent(REQUEST_VIDEO_MESSAGE);
    }
  };

  const removeAttachment = (index) => {
    // Functional update: a video upload in flight resolves asynchronously and appends via its own
    // setAttachments, so removing must be based on the latest state, not this render's snapshot.
    // The blob URL of the removed item is freed by the reconciliation effect above.
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Shared path for both recorded and picked videos: enforce caps, upload direct to Cloudinary, then
  // attach a reference (no base64 through Rails — see api/videoUploads.js).
  const handleVideoBlob = async (blob, { durationSeconds = null, filename, recorded = false }) => {
    if (attachments.length >= MAX_ATTACHMENTS) {
      globalThis.Qwoted.showToast({
        toastTitle: 'Too many files',
        toastBody: `You can attach up to ${MAX_ATTACHMENTS} files.`,
        variant: 'warning',
      });
      return;
    }
    if (durationSeconds !== null && durationSeconds > MAX_VIDEO_SECONDS) {
      globalThis.Qwoted.showToast({
        toastTitle: `Video too long (max ${MAX_VIDEO_SECONDS / 60} min)`,
        toastBody: filename,
        variant: 'warning',
      });
      return;
    }
    if (blob.size > MAX_VIDEO_SIZE_BYTES) {
      globalThis.Qwoted.showToast({
        toastTitle: `Video too large (max ${MAX_VIDEO_SIZE_MB} MB)`,
        toastBody: filename,
        variant: 'warning',
      });
      return;
    }

    // A local blob URL powers the composer thumbnail + pre-send preview (instant, no Cloudinary fetch).
    const localUrl = URL.createObjectURL(blob);
    localVideoUrlsRef.current.push(localUrl);

    setVideoUploading({
 filename, bytes: blob.size, progress: 0, localUrl,
});
    try {
      const ref = await uploadVideoToCloudinary(blob, {
        onProgress: (fraction) => setVideoUploading((current) => current && { ...current, progress: fraction }),
      });
      // Functional update: the upload took seconds, during which the user may have removed or added
      // other attachments. Appending to a snapshot of `attachments` from before the upload would
      // resurrect a removed attachment (or drop a newly added one) — so append to the latest state.
      setAttachments((prev) => [...prev, {
 ...ref, filename, recorded, localUrl,
}]);
    } catch (error) {
      globalThis.Qwoted.showToast({
        toastTitle: 'Video upload failed',
        toastBody: 'Please try again.',
        variant: 'warning',
      });
    } finally {
      setVideoUploading(null);
    }
  };

  // Videos picked via the paperclip route to the direct-upload path (100 MB cap), not the base64
  // file path (5 MB cap). MIME first; extension fallback for files the OS reports without a type.
  const isVideoFile = (file) => (file.type || '').startsWith('video/')
    || /\.(mp4|mov|webm|m4v)$/i.test(file.name);

  const handleFilesSelected = async (event) => {
    const allSelected = [...event.target.files];
    // Reset both pickers so the same file can be re-selected after removal.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
    if (allSelected.length === 0) {
      return;
    }

    const videos = allSelected.filter(isVideoFile);
    const files = allSelected.filter((file) => !isVideoFile(file));

    const tooLarge = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (tooLarge.length > 0) {
      globalThis.Qwoted.showToast({
        toastTitle: `File too large (max ${MAX_FILE_SIZE_MB} MB)`,
        toastBody: tooLarge.map((f) => f.name).join(', '),
        variant: 'warning',
      });
      return;
    }

    // Files first (this path writes a render-time snapshot; running it after the video loop
    // would clobber the videos' functional appends), then videos sequentially.
    const attachVideos = async () => {
      for (const video of videos) {
        // eslint-disable-next-line no-await-in-loop -- one upload chip at a time
        const durationSeconds = await probeVideoDuration(video);
        // eslint-disable-next-line no-await-in-loop -- one upload chip at a time
        await handleVideoBlob(video, { durationSeconds, filename: video.name });
      }
    };
    if (files.length === 0) {
      await attachVideos();
      return;
    }

    const read = files.map(
      (file) => new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve({ filename: file.name, bytes: file.size, file_cloudinary: reader.result });
          };
          reader.onerror = () => {
            resolve(null);
          };
          reader.readAsDataURL(file);
        }),
    );

    const selected = (await Promise.all(read)).filter(Boolean);
    const combined = [...attachments, ...selected];

    if (combined.length > MAX_ATTACHMENTS) {
      globalThis.Qwoted.showToast({
        toastTitle: 'Too many files',
        toastBody: `You can attach up to ${MAX_ATTACHMENTS} files.`,
        variant: 'warning',
      });
    }

    setAttachments(combined.slice(0, MAX_ATTACHMENTS));
    await attachVideos();
    // After picking files, drop the cursor straight into the composer so the user can type
    // without an extra click.
    document.getElementById('compose-message-field')?.focus();
  };

  const handleRecorded = (blob, { durationSeconds, mimeType }) => {
    const extension = mimeType?.includes('mp4') ? 'mp4' : 'webm';
    handleVideoBlob(blob, { durationSeconds, filename: `video-${Date.now()}.${extension}`, recorded: true });
  };

  const placeholderText = useMemo(() => {
    if (!conversation) return 'Compose a message...';

    const seenSlugs = new Set();
    const others = [
      ...(conversation.conversation_senders || []),
      ...(conversation.conversation_receivers || []),
    ].filter((p) => {
      if (p.slug === currentSlug || seenSlugs.has(p.slug)) return false;
      seenSlugs.add(p.slug);
      return true;
    });

    if (others.length === 0) return 'Compose a message...';

    const firstNames = others.map((p) => p.name?.split(' ')[0]);
    return `Message ${firstNames.join(', ')}`;
  }, [conversation, currentSlug]);

  const isSending = sendState === 'sending';
  const noText = noMessageText;
  const charCount = strippedMessageLength(messageContent);
  const overLimit = charCount > MAX_MESSAGE_LENGTH;
  const sendDisabled = isSending || disabled || overLimit || !!videoUploading || (noText && attachments.length === 0);
  // Disable every attach affordance while a video is uploading — adding/removing mid-upload raced
  // with the async append (see handleVideoBlob), so hold the attachment set steady until it resolves.
  const attachDisabled = disabled || isSending || !!videoUploading || attachments.length >= MAX_ATTACHMENTS;

  // The one Send button, wherever it renders: hosts (e.g. the pitch-reply modal footer) can pull
  // it out via sendButtonPortal so its gating (incl. video-upload holds) stays in one place.
  const sendButton = (
    <button
      type="button"
      className={classnames('btn btn-primary', { 'btn-sm': !sendButtonPortal })}
      onClick={sendMessage}
      // Mobile: don't take focus, or the composer collapses and hides this button before the click resolves.
      onMouseDown={(e) => e.preventDefault()}
      disabled={sendDisabled}
    >
      <i className="fa-solid fa-paper-plane me-1" /> Send
    </button>
  );

  return (
    <div className="message-composer mt-auto bg-white p-4">
      <div className={classnames('message-composer-inner', { 'over-limit': overLimit })}>
        <RichTextComposer
          id="compose-message-field"
          value={messageContent}
          onChange={handleMessageChange}
          placeholder={placeholderText}
          disabled={disabled || isSending}
          hasAttachments={attachments.length > 0 || !!videoUploading}
          toolbarAccessory={isMedia && (
            <button
              type="button"
              className={classnames(
                'btn btn-sm d-flex align-items-center gap-1 composer-request-video-btn',
                videoRequestIntent ? 'btn-outline-primary' : 'text-dark',
              )}
              style={{ fontSize: '12px' }}
              onClick={handleRequestVideo}
              disabled={disabled || isSending}
              aria-pressed={videoRequestIntent}
              title={videoRequestIntent ? 'Cancel the video request' : 'Mark this message as a video request'}
              data-testid="request-video-quick-reply"
            >
              {videoRequestIntent && <i className="fa-solid fa-check" aria-hidden="true" />}
              Request a video
            </button>
          )}
        >
          <div className="d-flex flex-column w-100 gap-2">
            {(attachments.length > 0 || videoUploading) && (
              <div
                className="d-flex flex-column gap-2 overflow-auto"
                data-testid="attachment-items"
                style={{ maxHeight: 200 }}
              >
                {attachments.map((attachment, index) => (attachment.resource_type === 'video' ? (
                    <VideoAttachmentItem
                      key={`${attachment.filename}-${index}`} // eslint-disable-line react/no-array-index-key
                      filename={attachment.filename}
                      src={attachment.localUrl}
                      onRemove={() => setVideoPendingDelete({ index, recorded: !!attachment.recorded })}
                    />
                  ) : (
                    <AttachmentItem
                      key={`${attachment.filename}-${index}`} // eslint-disable-line react/no-array-index-key
                      filename={attachment.filename}
                      bytes={attachment.bytes}
                      state={sendState}
                      onRemove={() => removeAttachment(index)}
                      onRetry={sendMessage}
                    />
                  )))}
                {videoUploading && (
                  <VideoAttachmentItem
                    filename={videoUploading.filename}
                    src={videoUploading.localUrl}
                    progress={videoUploading.progress}
                  />
                )}
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center gap-2">
              {/* Pull left by the actions(p-3) vs toolbar(p-2) padding gap so these align with the toolbar icons. */}
              <div className="d-flex align-items-center gap-1" style={{ marginLeft: '-0.5rem' }}>
                <input ref={fileInputRef} type="file" multiple className="d-none" onChange={handleFilesSelected} />
                <input
                  ref={imageInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="d-none"
                  onChange={handleFilesSelected}
                />
                <button
                  type="button"
                  className="btn btn-sm composer-toolbar-btn text-secondary"
                  title="Attach files"
                  aria-label="Attach files"
                  onClick={() => fileInputRef.current?.click()}
                  onMouseDown={(e) => e.preventDefault()}
                  disabled={attachDisabled}
                >
                  <i className="fa-solid fa-paperclip" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm composer-toolbar-btn text-secondary"
                  title="Attach photos"
                  aria-label="Attach photos"
                  onClick={() => imageInputRef.current?.click()}
                  onMouseDown={(e) => e.preventDefault()}
                  disabled={attachDisabled}
                >
                  <i className="fa-solid fa-image" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm border rounded d-flex align-items-center gap-2 text-dark ms-1 composer-record-btn"
                  style={{ fontSize: '12px' }}
                  title="Record a video"
                  aria-label="Record a video"
                  onClick={() => setShowRecorder(true)}
                  onMouseDown={(e) => e.preventDefault()}
                  disabled={attachDisabled}
                >
                  <i className="fa-solid fa-video" />
                  Record Video
                </button>
              </div>
              <div className="d-flex align-items-center gap-3">
                {charCount >= CHAR_COUNTER_REVEAL_AT && (
                  <span
                    className={classnames(
                      'composer-char-count paragraph-sm',
                      overLimit ? 'text-destructive-80' : 'text-neutral-60',
                    )}
                    data-testid="char-count"
                  >
                    {charCount}/{MAX_MESSAGE_LENGTH}
                  </span>
                )}
                {!sendButtonPortal && sendButton}
              </div>
            </div>
          </div>
        </RichTextComposer>
      </div>

      <VideoRecorderModal
        show={showRecorder}
        onHide={() => setShowRecorder(false)}
        onRecorded={handleRecorded}
        maxSeconds={MAX_VIDEO_SECONDS}
      />

      {sendButtonPortal && createPortal(sendButton, sendButtonPortal)}

      <ConfirmVideoDeleteModal
        show={!!videoPendingDelete}
        recorded={!!videoPendingDelete?.recorded}
        onCancel={() => setVideoPendingDelete(null)}
        onConfirm={() => {
          removeAttachment(videoPendingDelete.index);
          setVideoPendingDelete(null);
        }}
      />
    </div>
  );
}

MessageComposer.propTypes = {
  disabled: PropTypes.bool,
  sendButtonPortal: typeof Element === 'undefined' ? PropTypes.any : PropTypes.instanceOf(Element),
  conversation: PropTypes.shape({
    conversation_senders: PropTypes.arrayOf(PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string })),
    conversation_receivers: PropTypes.arrayOf(PropTypes.shape({ slug: PropTypes.string, name: PropTypes.string })),
  }),
};

export default MessageComposer;
